import type { FC } from "react";
import { ASSISTANT_AVATAR } from "@/theme/tokens";
import {
	AssistantAvatarEyes,
	AssistantAvatarMood,
	AssistantCharacterAccessory,
	type AssistantCharacterAvatar,
} from "@/ui/AssistantAvatar/assistantAvatarConfig";
import type { AssistantAvatarPointSide } from "@/ui/AssistantAvatar/assistantAvatarGeometry";
import type { AvatarBodyPalette } from "@/ui/AssistantAvatar/assistantAvatarPalette";
import { AvatarEyes } from "../shared/AvatarEyes";
import { AvatarMouth } from "../shared/AvatarMouth";
import { AvatarPointingHand, type PointingArmGeometry } from "../shared/AvatarPointingHand";
import { AvatarWaveHand } from "../shared/AvatarWaveHand";
import { CharacterAccessory, CharacterAccessoryBehind } from "./CharacterAccessory";
import { CharacterBody } from "./CharacterBody";
import { JackOLanternFace } from "./JackOLanternFace";

interface CharacterFigureProps {
	config: AssistantCharacterAvatar;
	mood: AssistantAvatarMood;
	body: AvatarBodyPalette;
	/** Unique, CSS-safe prefix for this instance's gradient id. */
	idPrefix: string;
	/** Screen side to point toward; the pointing arm replaces the needs-you wave. */
	point: AssistantAvatarPointSide | undefined;
}

// Leaves the body from behind at y 72, inside every shape's outline (the narrowest, bean, edges at x ≈ 20).
const CHARACTER_ARM: PointingArmGeometry = { shoulder: { x: 24, y: 72 }, bend: { x: 8, y: 64 }, hand: { x: -1, y: 50 }, handRadius: 8.5, armWidth: 8 };

/**
 * The character style: a colored shape with a face. Done swaps in happy eyes and takes the shades off; the pumpkin
 * stem carves the face into a jack-o'-lantern instead.
 */
export const CharacterFigure: FC<CharacterFigureProps> = ({ config, mood, body, idPrefix, point }) => {
	const isDone = mood === AssistantAvatarMood.Done;
	const eyes = isDone ? AssistantAvatarEyes.Happy : config.eyes;
	const accessory = isDone && config.accessory === AssistantCharacterAccessory.Shades ? AssistantCharacterAccessory.None : config.accessory;
	const isJackOLantern = accessory === AssistantCharacterAccessory.PumpkinStem;
	const gradientId = `${idPrefix}-body`;
	const fill = `url(#${gradientId})`;
	return (
		<>
			<defs>
				<linearGradient id={gradientId} x1={0} y1={0} x2={0} y2={1}>
					<stop offset={0} stopColor={body.light} />
					<stop offset={1} stopColor={body.main} />
				</linearGradient>
			</defs>
			{point !== undefined && (
				<AvatarPointingHand side={point} geometry={CHARACTER_ARM} armColor={body.main} handFill={fill} outline={ASSISTANT_AVATAR.FACE.HAND_OUTLINE_CHARACTER} />
			)}
			{mood === AssistantAvatarMood.NeedsYou && point === undefined && (
				<AvatarWaveHand cx={104} cy={74} rx={7} ry={9} fill={fill} outline={ASSISTANT_AVATAR.FACE.HAND_OUTLINE_CHARACTER} />
			)}
			<CharacterAccessoryBehind accessory={accessory} />
			<CharacterBody shape={config.shape} fill={fill} carved={isJackOLantern} idPrefix={idPrefix} />
			{isJackOLantern ? (
				<JackOLanternFace mood={mood} idPrefix={idPrefix} />
			) : (
				<>
					{accessory !== AssistantCharacterAccessory.Shades && <AvatarEyes type={eyes} ink={body.featureInk} />}
					<AvatarMouth mood={mood} ink={body.featureInk} />
				</>
			)}
			<CharacterAccessory accessory={accessory} />
		</>
	);
};
